import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookingDialog } from "@/components/BookingDialog";
import { EnrollExpertDialog } from "@/components/EnrollExpertDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Star, CalendarClock, Pencil, Check, X, Loader2, Search, GraduationCap,
  Briefcase, Building2, UserPlus, MessageSquare, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const Experts = () => {
  const { user, login } = useAuth();
  const [experts, setExperts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeIndustry, setActiveIndustry] = useState("All");
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", current_designation: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async (q, industry) => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (industry && industry !== "All") params.industry = industry;
      const res = await api.get("/experts", { params });
      setExperts(res.data);
    } catch (e) {
      toast.error("Failed to load experts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/experts/industries").then((r) => setIndustries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query, activeIndustry), 300);
    return () => clearTimeout(t);
  }, [query, activeIndustry, load]);

  const openBooking = (exp) => { setSelected(exp); setDialogOpen(true); };

  const startEdit = (exp) => {
    if (!user) { login(); return; }
    setEditingId(exp.id);
    setDraft({ name: exp.name, current_designation: exp.current_designation });
  };

  const saveEdit = async (id) => {
    setSavingEdit(true);
    try {
      const res = await api.put(`/experts/${id}`, draft);
      setExperts((prev) => prev.map((e) => (e.id === id ? res.data : e)));
      setEditingId(null);
      toast.success("Expert updated");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="bg-[#0F172A] min-h-[calc(100vh-4rem)] text-slate-100 font-body" data-testid="experts-page">
      <div className="max-w-7xl mx-auto px-5 py-14">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-xs font-medium mb-5">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Talk to the best minds in every industry
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">
            Engage with industry experts
          </h1>
          <p className="mt-4 text-slate-300 text-base sm:text-lg">
            Book a one-on-one appointment with vetted professionals — from a carbon black specialist to a banking & finance leader. Get real answers to real problems, not just resumes.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          {/* Main column */}
          <div className="lg:col-span-8">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search experts — e.g. carbon black, banking, legal, software…"
                className="pl-11 h-12 bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 rounded-full"
                data-testid="expert-search-input"
              />
            </div>

            {/* Industry filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {["All", ...industries].map((ind) => (
                <button
                  key={ind}
                  onClick={() => setActiveIndustry(ind)}
                  data-testid={`industry-${ind}`}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${activeIndustry === ind ? "bg-primary text-primary-foreground border-primary" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
                >
                  {ind}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : experts.length === 0 ? (
              <Card className="bg-[#1E293B] border-slate-700 text-slate-200 p-12 text-center" data-testid="no-experts">
                <Search className="h-9 w-9 mx-auto mb-3 text-slate-500" />
                <p className="font-medium">No experts match your search.</p>
                <p className="text-sm text-slate-400">Try a different keyword or industry.</p>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {experts.map((exp) => (
                  <Card key={exp.id} className="bg-[#1E293B] border-slate-700 text-slate-100 overflow-hidden group flex flex-col" data-testid={`expert-card-${exp.id}`}>
                    <div className="relative h-44 overflow-hidden">
                      <img src={exp.image} alt={exp.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide">{exp.industry}</div>
                      <div className="absolute top-3 right-3 bg-black/50 backdrop-blur rounded-full px-2.5 py-1 flex items-center gap-1 text-xs">
                        <Star className="h-3 w-3 text-accent fill-accent" /> {exp.rating}{exp.reviews ? ` · ${exp.reviews}` : ""}
                      </div>
                      {exp.status === "pending" && (
                        <div className="absolute bottom-3 left-3 bg-amber-500/90 text-amber-950 rounded-full px-2.5 py-0.5 text-[10px] font-semibold">Pending review</div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      {editingId === exp.id ? (
                        <div className="space-y-2">
                          <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className="bg-slate-800 border-slate-600 text-slate-100" data-testid={`edit-name-${exp.id}`} />
                          <Input value={draft.current_designation} onChange={(e) => setDraft((d) => ({ ...d, current_designation: e.target.value }))} className="bg-slate-800 border-slate-600 text-slate-100 text-sm" data-testid={`edit-designation-${exp.id}`} />
                          <div className="flex gap-2">
                            <Button size="sm" className="rounded-full flex-1" onClick={() => saveEdit(exp.id)} disabled={savingEdit} data-testid={`save-edit-${exp.id}`}>
                              <Check className="h-3.5 w-3.5 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-full bg-transparent border-slate-600" onClick={() => setEditingId(null)} data-testid={`cancel-edit-${exp.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-heading font-semibold text-lg leading-tight">{exp.name}</h3>
                            {user?.is_admin && (
                              <button onClick={() => startEdit(exp)} className="text-slate-400 hover:text-slate-100 transition-colors shrink-0" title="Edit name / designation" data-testid={`edit-expert-${exp.id}`}>
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-slate-300">
                            <p className="flex items-start gap-1.5"><Briefcase className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" /> <span><span className="text-slate-100 font-medium">{exp.current_designation}</span>{exp.company ? ` · ${exp.company}` : ""}</span></p>
                            {exp.previous_designation && (
                              <p className="flex items-start gap-1.5 text-slate-400"><Building2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>Prev: {exp.previous_designation}</span></p>
                            )}
                            {exp.education && (
                              <p className="flex items-start gap-1.5 text-slate-400"><GraduationCap className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>{exp.education}</span></p>
                            )}
                          </div>
                          <p className="text-sm text-slate-300 mt-3 line-clamp-2">{exp.bio}</p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {(exp.specialties || []).map((s) => (
                              <Badge key={s} variant="secondary" className="bg-slate-700 text-slate-200 hover:bg-slate-700 text-[10px]">{s}</Badge>
                            ))}
                          </div>
                          <div className="flex items-center justify-between mt-auto pt-4">
                            <span className="font-heading font-semibold text-accent text-sm">{exp.rate}</span>
                            <Button onClick={() => openBooking(exp)} size="sm" className="rounded-full font-semibold" data-testid={`book-${exp.id}`}>
                              <CalendarClock className="h-4 w-4 mr-1.5" /> Book slot
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <Card className="bg-gradient-to-br from-primary to-blue-700 border-none text-white p-6 sticky top-20" data-testid="enroll-sidebar">
              <UserPlus className="h-8 w-8 mb-3" />
              <h3 className="font-heading font-bold text-2xl leading-tight">Are you an expert?</h3>
              <p className="text-sm text-blue-100 mt-2">
                Enroll to offer paid consultations in your field — any industry welcome. List your current designation, education and specialties, and start taking appointments.
              </p>
              <div className="mt-5">
                <EnrollExpertDialog
                  industries={industries}
                  onEnrolled={() => load(query, activeIndustry)}
                  trigger={
                    <Button className="rounded-full w-full font-semibold bg-white text-primary hover:bg-blue-50" data-testid="sidebar-enroll-btn">
                      <UserPlus className="h-4 w-4 mr-1.5" /> Enroll as an Expert
                    </Button>
                  }
                />
              </div>
            </Card>

            <Card className="bg-[#1E293B] border-slate-700 text-slate-200 p-6">
              <MessageSquare className="h-7 w-7 text-accent mb-3" />
              <h4 className="font-heading font-semibold text-lg">How it works</h4>
              <ol className="mt-3 space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold shrink-0">1</span> Search or filter by industry to find the right expert.</li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold shrink-0">2</span> Pick an available time slot and describe your problem.</li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold shrink-0">3</span> Confirm your request — the expert reaches out to talk.</li>
              </ol>
            </Card>
          </aside>
        </div>
      </div>

      <BookingDialog expert={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

export default Experts;
